"use client"

import type React from "react"
import { useState, useCallback, useEffect } from "react"
import { X, Upload, FileText, CheckCircle, AlertCircle, Users } from "lucide-react"
import type { DynamicSchema, DynamicUser, DynamicCreateRequest } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import { createDynamicUser } from "../services/api"
import { addToQueue } from "../services/queue"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { BULK_FILE_ACCEPT, getBulkFileKind, parseBulkFile } from "../utils/bulkFileParser"

interface FieldMapping {
  csvHeader: string
  schemaField: string | null
  confidence: number
}

interface DuplicateRow {
  rowNumber: number
  value: string
  firstRowNumber: number
}

interface ConflictRow {
  rowNumber: number
  value: string
  /** Folio del alta sin folio que ya usa ese número como folio_client */
  occupiedBy: string
}

interface DuplicatesInfo {
  /** Filas que no se crean: ya existen, se repiten en el archivo o chocan con un alta sin folio */
  count: number
  fieldName: string
  /** Ya existen en el sistema */
  existingCount: number
  /** De las que ya existen, el archivo las trae impresas y aquí siguen sin imprimir: se marcan */
  toMarkCount: number
  /** Repiten el valor de una fila anterior del mismo archivo */
  inFile: DuplicateRow[]
  /** Su folio lo tiene un alta sin folio de otra persona (folio_client = id): no se crean */
  conflicts: ConflictRow[]
}

// Valores que se toman como "sí" en las columnas booleanas del archivo
const TRUE_VALUES = ['true', '1', 'sí', 'si', 'yes', 'verdadero']
// Columnas que indican que el gafete ya está impreso; el back deja printed = 1 con cualquiera de las dos
const PRINTED_FIELDS = ['printed', 'pre_impression_client']

interface DynamicBulkUploadModalProps {
  schema: DynamicSchema
  existingUsers: DynamicUser[]
  onClose: () => void
  onSuccess: (users: DynamicUser[]) => void
  /** El servidor creó los usuarios pero no los devolvió: recargar la lista */
  onServerCreated?: () => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  cancelCurrentFetch: () => void
}

const DynamicBulkUploadModal: React.FC<DynamicBulkUploadModalProps> = ({
  schema,
  existingUsers,
  onClose,
  onSuccess,
  onServerCreated,
  showToast,
  cancelCurrentFetch,
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<any[]>([])
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])
  const [step, setStep] = useState<'upload' | 'mapping' | 'processing'>('upload')
  const [filteredCount, setFilteredCount] = useState<number>(0)
  const [duplicatesInfo, setDuplicatesInfo] = useState<DuplicatesInfo | null>(null)
  const isOnline = useNetworkStatus()

  // Función para limpiar y normalizar texto para comparación
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9]/g, '') // Solo letras y números
  }

  // Función para encontrar la mejor coincidencia entre encabezado CSV y campo del schema
  const findBestMatch = (csvHeader: string): { field: string | null; confidence: number } => {
    const normalizedHeader = normalizeText(csvHeader)
    
    let bestMatch: { field: string | null; confidence: number } = { field: null, confidence: 0 }

    // Buscar en todos los campos del schema
    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      // Skip ID field
      if (fieldName === 'id') return

      const normalizedFieldName = normalizeText(fieldName)
      const normalizedLabel = normalizeText(fieldConfig.label)

      // Coincidencia exacta con nombre del campo
      if (normalizedHeader === normalizedFieldName) {
        bestMatch = { field: fieldName, confidence: 100 }
        return
      }

      // Coincidencia exacta con label
      if (normalizedHeader === normalizedLabel) {
        bestMatch = { field: fieldName, confidence: 95 }
        return
      }

      // Coincidencia parcial con nombre del campo
      if (normalizedHeader.includes(normalizedFieldName) || normalizedFieldName.includes(normalizedHeader)) {
        const confidence = Math.max(bestMatch.confidence, 80)
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: fieldName, confidence }
        }
      }

      // Coincidencia parcial con label
      if (normalizedHeader.includes(normalizedLabel) || normalizedLabel.includes(normalizedHeader)) {
        const confidence = Math.max(bestMatch.confidence, 75)
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: fieldName, confidence }
        }
      }

      // Coincidencias específicas comunes
      const commonMappings: { [key: string]: string } = {
        'email': 'email',
        'correo': 'email',
        'mail': 'email',
        'telefono': 'telefono',
        'phone': 'telefono',
        'tel': 'telefono',
        'nombre': 'nombre',
        'name': 'nombre',
        'firstname': 'nombre',
        'apellido': 'apellido',
        'lastname': 'apellido',
        'surname': 'apellido',
        'empresa': 'empresa',
        'company': 'empresa',
        'organizacion': 'empresa',
        'organization': 'empresa',
        'cargo': 'cargo',
        'position': 'cargo',
        'puesto': 'cargo',
        'job': 'cargo',
        'categoria': 'categoria',
        'category': 'categoria',
        'tipo': 'categoria',
        'type': 'categoria',
        'ciudad': 'ciudad',
        'city': 'ciudad',
        'pais': 'pais',
        'country': 'pais',
        'industria': 'industria',
        'industry': 'industria',
        'qr': 'codigo_qr',
        'codigoqr': 'codigo_qr',
        'codigo': 'codigo_qr'
      }

      const mappedField = commonMappings[normalizedHeader]
      if (mappedField && schema[mappedField]) {
        const confidence = 90
        if (confidence > bestMatch.confidence) {
          bestMatch = { field: mappedField, confidence }
        }
      }
    })

    return bestMatch
  }

  // Procesar archivo Excel (.xlsx, .xls) o CSV
  const handleFileUpload = useCallback(async (file: File) => {
    if (!getBulkFileKind(file.name)) {
      showToast("error", "Solo se permiten archivos Excel (.xlsx, .xls) o CSV")
      return
    }

    let parsed
    try {
      parsed = await parseBulkFile(file)
    } catch (error) {
      console.error('Error leyendo el archivo de carga masiva:', error)
      showToast("error", "No se pudo leer el archivo. Revisa que sea un Excel o CSV válido")
      return
    }

    if (parsed.headers.length === 0 || parsed.rows.length === 0) {
      showToast("error", "El archivo debe tener una fila de encabezados y al menos una fila de datos")
      return
    }

    const parsedData = parsed.rows.map(row => ({ ...row.values, _rowIndex: row.rowNumber }))
    setCsvFile(file)
    setCsvData(parsedData)

    // Crear mapeos automáticos
    const mappings: FieldMapping[] = parsed.headers.map(header => {
      const match = findBestMatch(header)
      return {
        csvHeader: header,
        schemaField: match.field,
        confidence: match.confidence
      }
    })

    // Si dos columnas apuntan al mismo campo (ej. "Pago" y "Fecha de Pago"), solo se queda la de mayor confianza:
    // al crear, la última columna mapeada pisaría a las demás sin avisar
    const bestByField = new Map<string, FieldMapping>()
    mappings.forEach(mapping => {
      if (!mapping.schemaField) return
      const best = bestByField.get(mapping.schemaField)
      if (!best || mapping.confidence > best.confidence) bestByField.set(mapping.schemaField, mapping)
    })
    mappings.forEach(mapping => {
      if (mapping.schemaField && bestByField.get(mapping.schemaField) !== mapping) {
        mapping.schemaField = null
        mapping.confidence = 0
      }
    })

    setFieldMappings(mappings)
    setStep('mapping')

    const sheetNote = parsed.sheetCount && parsed.sheetCount > 1 ? ` (hoja "${parsed.sheetName}")` : ''
    showToast("success", `Archivo procesado: ${parsedData.length} registros encontrados${sheetNote}`)
  }, [schema, showToast])

  // Separar las filas del archivo que se pueden crear de las duplicadas por el campo fixed.
  // El back inserta el lote en una sola transacción: un solo duplicado cancela la carga completa,
  // por eso se omiten antes de enviar tanto los que ya existen como los repetidos dentro del archivo.
  // Con el folio, los que ya existen y el archivo trae impresos sí se envían (toMark): el back no los
  // vuelve a crear, solo los marca como impresos.
  const findDuplicates = useCallback((rows: any[], fixedField: string, csvHeader: string, mappings: FieldMapping[]) => {
    // Para el folio se compara el número que el back guarda en folio_client (INT, único):
    // "0010", "10" y el usuario con folio CF0010 son el mismo folio
    const duplicateKey = (value: unknown): string => {
      const text = String(value).trim().toLowerCase()
      return fixedField === 'folio' && /^\d+$/.test(text) ? text.replace(/^0+(?=\d)/, '') : text
    }

    const existingKeys = new Set<string>()
    // folio_client → usuarios locales con ese número (el back busca igual, por folio_client)
    const usersByFolioClient = new Map<string, DynamicUser[]>()
    existingUsers.forEach(user => {
      const value = SchemaService.getFieldValue(user, fixedField)
      if (value !== null && value !== undefined && value !== '') {
        existingKeys.add(duplicateKey(value))
      }
      // Los usuarios creados sin folio usan su id como folio_client
      if (fixedField === 'folio' && user.folio_client) {
        const folioClient = String(user.folio_client)
        existingKeys.add(folioClient)
        usersByFolioClient.set(folioClient, [...(usersByFolioClient.get(folioClient) ?? []), user])
      }
    })

    const printedHeaders = mappings
      .filter(m => m.schemaField && PRINTED_FIELDS.includes(m.schemaField))
      .map(m => m.csvHeader)
    const isPrintedInFile = (row: any) =>
      printedHeaders.some(header => TRUE_VALUES.includes(String(row[header] ?? '').trim().toLowerCase()))

    const firstRowByKey = new Map<string, number>()
    const inFile: DuplicateRow[] = []
    const conflicts: ConflictRow[] = []
    const toMark: any[] = []
    const toMarkKeys = new Set<string>()
    let existingCount = 0
    const kept = rows.filter(row => {
      const csvValue = row[csvHeader]
      if (!csvValue || String(csvValue).trim() === '') {
        return true // Mantener filas sin valor en campo fixed
      }

      const key = duplicateKey(csvValue)
      if (existingKeys.has(key)) {
        const matches = usersByFolioClient.get(key) ?? []
        const clientUsers = matches.filter(user => String(user.id) !== key)
        if (matches.length > 0 && clientUsers.length === 0) {
          conflicts.push({ rowNumber: row._rowIndex, value: String(csvValue).trim(), occupiedBy: String(matches[0].folio ?? '') })
          return false
        }
        existingCount++
        if (!toMarkKeys.has(key) && isPrintedInFile(row) && clientUsers.some(user => !user.printed)) {
          toMarkKeys.add(key)
          toMark.push(row)
        }
        return false
      }
      const firstRowNumber = firstRowByKey.get(key)
      if (firstRowNumber !== undefined) {
        inFile.push({ rowNumber: row._rowIndex, value: String(csvValue).trim(), firstRowNumber })
        return false
      }
      firstRowByKey.set(key, row._rowIndex)
      return true
    })

    const count = existingCount + inFile.length + conflicts.length
    const info: DuplicatesInfo | null = count > 0
      ? { count, fieldName: schema[fixedField]?.label || fixedField, existingCount, toMarkCount: toMark.length, inFile, conflicts }
      : null
    return { kept, toMark, info }
  }, [existingUsers, schema])

  // Calcular duplicados en tiempo real
  const calculateDuplicates = useCallback((mappings: FieldMapping[]) => {
    const fixedField = SchemaService.getFixedField(schema)
    const fixedFieldMapping = mappings.find(m => m.schemaField === fixedField)

    if (!fixedField || !fixedFieldMapping) {
      setFilteredCount(csvData.length)
      setDuplicatesInfo(null)
      return
    }

    const { kept, info } = findDuplicates(csvData, fixedField, fixedFieldMapping.csvHeader, mappings)
    setFilteredCount(kept.length)
    setDuplicatesInfo(info)
  }, [csvData, schema, findDuplicates])

  // Efecto para calcular duplicados automáticamente cuando se cargan los datos
  useEffect(() => {
    if (csvData.length > 0 && fieldMappings.length > 0) {
      calculateDuplicates(fieldMappings)
    }
  }, [csvData, fieldMappings, calculateDuplicates])

  // Actualizar mapeo de campo
  const updateFieldMapping = (csvHeader: string, schemaField: string | null) => {
    const newMappings = fieldMappings.map(mapping => 
      mapping.csvHeader === csvHeader 
        ? { ...mapping, schemaField, confidence: schemaField ? 100 : 0 }
        : mapping
    )
    setFieldMappings(newMappings)
    
    // Recalcular duplicados con los nuevos mapeos
    calculateDuplicates(newMappings)
  }

  // Procesar y crear usuarios
  const handleCreateUsers = async () => {
    if (csvData.length === 0) return

    setStep('processing')
    
    try {
      // Validar que al menos haya algunos mapeos
      const validMappings = fieldMappings.filter(m => m.schemaField !== null)
      if (validMappings.length === 0) {
        showToast("error", "Debe mapear al menos un campo")
        setStep('mapping')
        return
      }

      // Obtener el campo fixed del schema
      const fixedField = SchemaService.getFixedField(schema)
      
      // Verificar si hay mapeo del campo fixed
      const fixedFieldMapping = fieldMappings.find(m => m.schemaField === fixedField)
      
      // Filtrar duplicados basándose en el campo fixed si existe
      let filteredCsvData = csvData
      let duplicatesCount = 0
      // Filas nuevas (sin contar las que ya existen y solo se marcan como impresas)
      let newCount = csvData.length

      if (fixedField && fixedFieldMapping) {
        console.log(`🔍 Verificando duplicados usando campo fixed: ${fixedField}`)

        const originalCount = csvData.length
        const { kept, toMark, info } = findDuplicates(csvData, fixedField, fixedFieldMapping.csvHeader, fieldMappings)
        filteredCsvData = [...kept, ...toMark]
        newCount = kept.length
        duplicatesCount = (info?.count ?? 0) - toMark.length
        if (info && info.inFile.length > 0) {
          console.log(`🚫 Filas que repiten ${fixedField} dentro del archivo:`, info.inFile)
        }
        if (info && info.conflicts.length > 0) {
          console.log(`🚫 Folios ocupados por altas sin folio (no se crean):`, info.conflicts)
        }

        console.log(`✂️ Filtrado completado: ${originalCount} → ${kept.length} nuevos + ${toMark.length} por marcar como impresos (${duplicatesCount} duplicados omitidos)`)

        // Informar al usuario sobre duplicados encontrados
        if (duplicatesCount > 0) {
          showToast("info", `Se omitieron ${duplicatesCount} registros duplicados basados en ${schema[fixedField]?.label || fixedField}`)
        }

        // Si todas las filas fueron filtradas por duplicados
        if (filteredCsvData.length === 0) {
          showToast("error", "Todos los registros del archivo ya existen en el sistema")
          setStep('mapping')
          return
        }
      }

      // Construir usuarios usando los datos filtrados
      const users: DynamicCreateRequest[] = filteredCsvData.map((row) => {
        const user: DynamicCreateRequest = {
          //folio: 0,
          name: '',
          last_name: '',
          email: '',
          phone_number: '',
          printed: 0,
          print_date: '',
          number_of_prints: 0,
          extra: {}
        }

        // Aplicar mapeos
        fieldMappings.forEach(mapping => {
          if (mapping.schemaField && row[mapping.csvHeader] !== undefined) {
            const value = row[mapping.csvHeader]
            const fieldConfig = schema[mapping.schemaField]
            
            // Procesar valor según tipo de campo
            let processedValue: any = value
            
            if (fieldConfig) {
              switch (fieldConfig.type) {
                case 'number':
                  processedValue = value === '' ? 0 : Number(value) || 0
                  break
                case 'boolean':
                  processedValue = TRUE_VALUES.includes(value.toLowerCase())
                  break
                case 'string':
                case 'enum':
                default:
                  // Normalización específica por campo
                  if (mapping.schemaField === 'email') {
                    processedValue = value.toLowerCase().trim()
                  } else if (mapping.schemaField === 'telefono') {
                    processedValue = value.replace(/[^\d\s()+-]/g, "")
                  } else if (mapping.schemaField === 'nombre' || mapping.schemaField === 'apellido') {
                    processedValue = value.replace(/\s+/g, " ").trim()
                  } else {
                    processedValue = value.trim()
                  }
                  break
              }
            }

            // Asignar a campo principal o extra
            const mainFields = ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints']
            if (mainFields.includes(mapping.schemaField)) {
              (user as any)[mapping.schemaField] = processedValue
            } else {
              user.extra[mapping.schemaField] = processedValue
            }
          }
        })

        return user
      })

      // Cancelar fetch actual
      cancelCurrentFetch()

      // Crear usuarios usando el sistema existente
      const createUsersPayload = users

      const createUsers = async () => {
        if (isOnline) {
          try {
            const result = await createDynamicUser(createUsersPayload)
            
            // Manejar respuesta
            if (Array.isArray(result)) {
              // Respuesta exitosa con array de usuarios creados
              showToast("success", `${result.length} usuarios creados exitosamente`)
              onSuccess(result)
              onClose()
            } else {
              // El back responde al lote con { creados, marcados_impresos, omitidos, total_enviados, errores } en lugar de la lista
              const summary = result as unknown as { creados?: number; marcados_impresos?: number; errores?: string[] }
              const errores = Array.isArray(summary.errores) ? summary.errores : []
              const creados = summary.creados ?? 0
              const marcados = summary.marcados_impresos ?? 0
              const marcadosText = marcados > 0 ? ` y se marcaron ${marcados} como impresos` : ''
              if (errores.length > 0 && !creados && !marcados) {
                // El back cancela el lote completo ante un error de la BD, o todas las filas chocaron con altas sin folio
                console.warn("⚠️ Errores en la carga masiva:", errores)
                showToast("error", `No se creó ni se marcó ningún usuario (detalle en consola). Revisa el archivo e intenta de nuevo.`)
                setStep('mapping')
                return
              } else if (errores.length > 0) {
                console.warn("⚠️ Errores en la carga masiva:", errores)
                showToast("error", `Se crearon ${creados} de ${newCount} usuarios${marcadosText}. Algunos registros no se pudieron procesar (detalle en consola).`)
              } else if (creados > 0) {
                showToast("success", `${creados} usuarios creados exitosamente${marcadosText}`)
              } else if (marcados > 0) {
                showToast("success", `Se marcaron ${marcados} usuarios como impresos`)
              } else {
                // La copia local estaba atrasada: en el servidor ya estaban creados e impresos
                showToast("info", "No hubo cambios: los registros ya estaban creados e impresos")
              }
              onServerCreated?.()
              onClose()
            }
          } catch (networkError: any) {
            console.warn("Error en creación masiva:", networkError)
            
            // Verificar si es un error 207 (algunos usuarios no se pudieron crear)
            if (networkError.message && networkError.message.includes("207")) {
              // Error 207: Algunos usuarios no se pudieron crear (probablemente duplicados)
              // NO agregar a la cola porque volverá a fallar
              showToast("error", "Algunos usuarios no se pudieron crear (posibles duplicados). Revisa los datos e intenta nuevamente.")
              setStep('mapping') // Volver al mapeo para que el usuario pueda revisar
              return
            }
            
            // Para otros errores de red, agregar a la cola
            await addToQueue({
              type: "create_dynamic_user_bulk",
              data: createUsersPayload as any,
              timestamp: Date.now(),
            })
            showToast("info", "Usuarios guardados localmente, se sincronizarán cuando haya conexión")
            onClose()
          }
        } else {
          await addToQueue({
            type: "create_dynamic_user_bulk",
            data: createUsersPayload as any,
            timestamp: Date.now(),
          })
          showToast("info", "Usuarios guardados localmente, se sincronizarán cuando haya conexión")
          onClose()
        }
      }

      await createUsers()
      
    } catch (error) {
      console.error('Error creating users:', error)
      showToast("error", "Error al crear los usuarios")
      setStep('mapping')
    } finally {
    }
  }

  // Obtener campos disponibles del schema (excluyendo ID)
  const availableFields = Object.entries(schema)
    .filter(([fieldName]) => fieldName !== 'id')
    .map(([fieldName, fieldConfig]) => ({
      value: fieldName,
      label: fieldConfig.label
    }))

  // Ya existen y se marcarán como impresos; sin nuevos ni por marcar no hay nada que enviar
  const markCount = duplicatesInfo?.toMarkCount ?? 0
  const nothingToSend = filteredCount === 0 && markCount === 0

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white/40 backdrop-blur-sm px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Carga Masiva de Usuarios
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Step Upload */}
            {step === 'upload' && (
              <div className="space-y-6">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <h4 className="mt-2 text-lg font-medium text-gray-900">Subir archivo Excel o CSV</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Selecciona un archivo Excel (.xlsx, .xls) o CSV con los datos de los usuarios
                  </p>
                </div>

                {/* File upload area */}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors"
                  onDrop={(e) => {
                    e.preventDefault()
                    const file = e.dataTransfer.files[0]
                    if (file) handleFileUpload(file)
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <FileText className="mx-auto h-8 w-8 text-gray-400 mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Arrastra tu archivo aquí o
                    </p>
                    <label className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-800 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 cursor-pointer">
                      <Upload className="h-4 w-4 mr-2" />
                      Seleccionar archivo
                      <input
                        type="file"
                        accept={BULK_FILE_ACCEPT}
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleFileUpload(file)
                        }}
                        className="sr-only"
                      />
                    </label>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Requisitos del archivo
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Primera fila debe contener los encabezados</li>
                          <li>Excel: se lee la primera hoja visible; las fechas se cargan como AAAA-MM-DD HH:MM:SS</li>
                          <li>CSV: separado por comas (,), punto y coma (;) o tabulaciones; los valores con separadores van entre comillas (Excel lo hace solo)</li>
                          <li>CSV: de preferencia guardado como "CSV UTF-8"</li>
                          <li>Los encabezados se mapearán automáticamente con los campos disponibles</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step Mapping */}
            {step === 'mapping' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Mapeo de Campos</h4>
                    <p className="text-sm text-gray-500">
                      Archivo: {csvFile?.name} - {csvData.length} registros
                    </p>
                  </div>
                  <button
                    onClick={() => setStep('upload')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Cambiar archivo
                  </button>
                </div>

                <div className="bg-amber-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-amber-700">
                        Revisa y corrige el mapeo automático de campos. Los campos sin mapear se ignorarán.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información de duplicados */}
                {duplicatesInfo && (
                  <div className={`p-4 rounded-xl ${nothingToSend ? 'bg-red-50' : 'bg-orange-50'}`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className={`h-5 w-5 ${nothingToSend ? 'text-red-400' : 'text-orange-400'}`} />
                      </div>
                      <div className="ml-3">
                        <h3 className={`text-sm font-medium ${nothingToSend ? 'text-red-800' : 'text-orange-800'}`}>
                          {nothingToSend ? 'Todos los registros son duplicados' : 'Duplicados detectados'}
                        </h3>
                        <p className={`text-sm mt-1 ${nothingToSend ? 'text-red-700' : 'text-orange-700'}`}>
                          {nothingToSend ? (
                            <>
                              <strong>Todos los {duplicatesInfo.count} registros del archivo están duplicados</strong> en el campo <strong>{duplicatesInfo.fieldName}</strong>.
                              No se puede continuar con la carga masiva.
                            </>
                          ) : (
                            <>
                              Se encontraron <strong>{duplicatesInfo.count} registros duplicados</strong> en el campo <strong>{duplicatesInfo.fieldName}</strong>
                              {duplicatesInfo.existingCount > 0 && <> ({duplicatesInfo.existingCount} ya existen en el sistema)</>}.
                              {filteredCount > 0
                                ? <> Solo se crearán <strong>{filteredCount} usuarios nuevos</strong>, omitiendo automáticamente los duplicados.</>
                                : <> No hay usuarios nuevos.</>}
                            </>
                          )}
                        </p>
                        {markCount > 0 && (
                          <p className="text-sm mt-1 text-orange-700">
                            De los que ya existen, <strong>{markCount} se marcarán como impresos</strong>: el archivo los trae impresos y en el sistema siguen sin imprimir.
                          </p>
                        )}
                        {duplicatesInfo.conflicts.length > 0 && (
                          <div className={`text-sm mt-2 ${nothingToSend ? 'text-red-700' : 'text-orange-700'}`}>
                            <p>
                              Folios ocupados por un alta sin folio de otra persona (no se crean; hay que corregirlos a mano):
                            </p>
                            <ul className="list-disc ml-5 mt-1">
                              {duplicatesInfo.conflicts.slice(0, 10).map(row => (
                                <li key={row.rowNumber}>
                                  Fila {row.rowNumber}: {duplicatesInfo.fieldName} "{row.value}" lo usa {row.occupiedBy || 'otro registro'}
                                </li>
                              ))}
                              {duplicatesInfo.conflicts.length > 10 && (
                                <li>…y {duplicatesInfo.conflicts.length - 10} más (detalle en consola al crear)</li>
                              )}
                            </ul>
                          </div>
                        )}
                        {duplicatesInfo.inFile.length > 0 && (
                          <div className={`text-sm mt-2 ${nothingToSend ? 'text-red-700' : 'text-orange-700'}`}>
                            <p>
                              Repetidos dentro del archivo (se crea solo la primera aparición; revisa si son personas distintas):
                            </p>
                            <ul className="list-disc ml-5 mt-1">
                              {duplicatesInfo.inFile.slice(0, 10).map(row => (
                                <li key={row.rowNumber}>
                                  Fila {row.rowNumber}: {duplicatesInfo.fieldName} "{row.value}", igual que la fila {row.firstRowNumber}
                                </li>
                              ))}
                              {duplicatesInfo.inFile.length > 10 && (
                                <li>…y {duplicatesInfo.inFile.length - 10} más (detalle en consola al crear)</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Información adicional cuando no hay duplicados pero sí campo fixed */}
                {!duplicatesInfo && filteredCount === csvData.length && SchemaService.getFixedField(schema) && fieldMappings.find(m => m.schemaField === SchemaService.getFixedField(schema)) && (
                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          Sin duplicados detectados
                        </h3>
                        <p className="text-sm text-green-700 mt-1">
                          Todos los <strong>{csvData.length} registros</strong> son únicos basados en el campo <strong>{schema[SchemaService.getFixedField(schema)!]?.label || SchemaService.getFixedField(schema)}</strong>.
                          Se crearán todos los usuarios.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mappings */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {fieldMappings.map((mapping, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Encabezado del archivo
                        </label>
                        <div className="p-2 bg-gray-50 rounded-lg text-sm">
                          {mapping.csvHeader}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Campo del Sistema
                        </label>
                        <select
                          value={mapping.schemaField || ''}
                          onChange={(e) => updateFieldMapping(mapping.csvHeader, e.target.value || null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Sin mapear</option>
                          {availableFields.map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confianza
                        </label>
                        <div className="flex items-center space-x-2">
                          <div className={`h-2 w-full rounded-full ${
                            mapping.confidence >= 80 ? 'bg-green-200' : 
                            mapping.confidence >= 60 ? 'bg-yellow-200' : 'bg-red-200'
                          }`}>
                            <div 
                              className={`h-2 rounded-full ${
                                mapping.confidence >= 80 ? 'bg-green-500' : 
                                mapping.confidence >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${mapping.confidence}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-500">{mapping.confidence}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateUsers}
                    disabled={fieldMappings.filter(m => m.schemaField !== null).length === 0 || nothingToSend}
                    className="inline-flex items-center px-4 py-2 bg-green-600/20 backdrop-blur-sm border border-green-400/30 text-green-800 text-sm font-medium rounded-xl hover:bg-green-600/30 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    {filteredCount === 0 && markCount > 0
                      ? <>Marcar {markCount} impresos</>
                      : <>Crear {filteredCount > 0 ? filteredCount : csvData.length} Usuarios{markCount > 0 && <> · marcar {markCount} impresos</>}</>}
                    {duplicatesInfo && duplicatesInfo.count - markCount > 0 && (
                      <span className="ml-1 text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full">
                        {duplicatesInfo.count - markCount} omitidos
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step Processing */}
            {step === 'processing' && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                  </div>
                  <h4 className="mt-4 text-lg font-medium text-gray-900">Creando usuarios...</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Procesando {csvData.length} registros. Esto puede tomar unos momentos.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-xl">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        {isOnline 
                          ? "Creando usuarios en línea..." 
                          : "Sin conexión: los usuarios se guardarán localmente y se sincronizarán cuando haya conexión"
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicBulkUploadModal
